import { Check, ChevronDown, ChevronUp, Copy, Plus, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import type { CampaignStatsDto } from "@/shared/api/outboundApi";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { IconButton } from "@/shared/ui/icon-button";
import { Input } from "@/shared/ui/input";
import { StatusBadge } from "@/shared/ui/status-badge";
import { toast } from "@/shared/ui/toast";
import {
  campaignProgress,
  campaignStatusUi,
  contactStatusUi,
  newKeyValueRow,
  payloadEntries,
  type KeyValueRow,
} from "./outboundLabels";

/* ------------------------------------------------------------ badges -- */

export function CampaignStatusBadge({ status, size = "sm" }: { status?: string | null; size?: "sm" | "md" }) {
  return <StatusBadge meta={campaignStatusUi(status)} size={size} />;
}

export function ContactStatusBadge({ status, variant }: { status?: string | null; variant?: "badge" | "dot" }) {
  return <StatusBadge meta={contactStatusUi(status)} variant={variant} />;
}

/* -------------------------------------------------------------- copy -- */

export function CopyButton({
  text,
  label = "Copiar",
  className,
  iconOnly,
}: {
  text: string;
  label?: string;
  className?: string;
  /** Só ícone (blocos de código, células). */
  iconOnly?: boolean;
}) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      window.setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error("Não foi possível copiar para a área de transferência.");
    }
  };
  if (iconOnly) {
    return (
      <IconButton
        icon={done ? Check : Copy}
        label={done ? "Copiado" : label}
        size="sm"
        className={cn(done && "text-success-fg", className)}
        onClick={() => void copy()}
      />
    );
  }
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      icon={done ? Check : Copy}
      className={cn("shrink-0", className)}
      onClick={() => void copy()}
      aria-label={label}
      title={label}
    >
      <span aria-live="polite">{done ? "Copiado" : label}</span>
    </Button>
  );
}

/* --------------------------------------------------------- código ---- */

type TokenKind = "comment" | "string" | "key" | "number" | "keyword" | "flag" | "plain";

const TOKEN_CLASS: Record<TokenKind, string> = {
  comment: "text-fg-subtle italic",
  string: "text-success-fg",
  key: "text-info-fg",
  number: "text-warning-fg",
  keyword: "text-brand-accent font-medium",
  flag: "text-brand-accent",
  plain: "",
};

const KEYWORDS =
  "const|let|var|import|from|return|if|else|def|not|and|or|true|false|null|None|True|False|async|await|new|export|function|curl";

function tokenRegex(language?: string) {
  const comment = language === "python" || language === "bash" ? String.raw`#[^\n]*` : String.raw`\/\/[^\n]*`;
  return new RegExp(
    [
      `(${comment})`,
      String.raw`("(?:\\.|[^"\\\n])*"|'(?:\\.|[^'\\\n])*'|` + "`(?:\\\\.|[^`\\\\])*`)",
      String.raw`(\b-?\d+(?:\.\d+)?\b)`,
      `\\b(${KEYWORDS})\\b`,
      String.raw`((?<=\s)--?[A-Za-z][\w-]*)`,
    ].join("|"),
    "g",
  );
}

/** Realce leve de sintaxe (json/bash/js/python) com cores de token — sem dependências. */
function highlight(code: string, language?: string): ReactNode[] {
  if (language === "text") return [code];
  const re = tokenRegex(language);
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(code))) {
    if (m.index > last) out.push(code.slice(last, m.index));
    let kind: TokenKind = "plain";
    if (m[1]) kind = "comment";
    else if (m[2]) kind = /^\s*:/.test(code.slice(re.lastIndex)) ? "key" : "string";
    else if (m[3]) kind = "number";
    else if (m[4]) kind = "keyword";
    else if (m[5]) kind = "flag";
    out.push(
      <span key={i++} className={TOKEN_CLASS[kind]}>
        {m[0]}
      </span>,
    );
    last = re.lastIndex;
    if (m[0].length === 0) re.lastIndex++;
  }
  if (last < code.length) out.push(code.slice(last));
  return out;
}

const COLLAPSE_LINES = 16;

export function CodeBlock({
  code,
  title,
  language,
  className,
  collapsible = true,
}: {
  code: string;
  title?: string;
  language?: string;
  className?: string;
  /** Blocos longos começam recolhidos (com "Mostrar tudo"). */
  collapsible?: boolean;
}) {
  const lines = code.split("\n").length;
  const long = collapsible && lines > COLLAPSE_LINES;
  const [expanded, setExpanded] = useState(false);
  return (
    <figure className={cn("min-w-0 overflow-hidden rounded-lg border border-border bg-surface-sunken", className)}>
      <figcaption className="flex min-h-10 items-center justify-between gap-2 border-b border-border bg-surface px-3 py-1">
        <span className="flex min-w-0 items-center gap-2 text-body-sm font-medium text-fg-muted">
          <span className="truncate">{title}</span>
          {language ? (
            <span className="shrink-0 rounded-sm bg-surface-sunken px-1.5 font-mono text-caption text-fg-subtle">{language}</span>
          ) : null}
        </span>
        <CopyButton text={code} label={title ? `Copiar ${title}` : "Copiar código"} iconOnly />
      </figcaption>
      <pre
        className={cn(
          "overflow-auto p-4 font-mono text-caption leading-relaxed text-fg",
          long && !expanded ? "max-h-72" : "max-h-[36rem]",
        )}
        tabIndex={0}
        aria-label={title ? `Código: ${title}` : "Código"}
      >
        <code>{highlight(code, language)}</code>
      </pre>
      {long ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex w-full items-center justify-center gap-1 border-t border-border bg-surface py-1.5 text-body-sm font-medium text-fg-muted hover:bg-surface-hover hover:text-fg"
        >
          {expanded ? <ChevronUp className="h-4 w-4" aria-hidden /> : <ChevronDown className="h-4 w-4" aria-hidden />}
          {expanded ? "Recolher" : `Mostrar tudo (${lines} linhas)`}
        </button>
      ) : null}
    </figure>
  );
}

/* ------------------------------------------------------ distribuição -- */

type Segment = { key: string; label: string; value: number; className: string };

/** Ordem do "ciclo de vida": abertos → sucesso → desvios → finais negativos. Cores = tokens de status. */
function statusSegments(s: CampaignStatsDto): Segment[] {
  return [
    { key: "pending", label: "Pendentes", value: (s.pending ?? 0) + (s.scheduled ?? 0), className: "bg-border-strong" },
    { key: "inflight", label: "Em curso", value: (s.dialing ?? 0) + (s.answered ?? 0), className: "bg-status-dialing" },
    { key: "connected", label: "Conectadas", value: s.connected ?? 0, className: "bg-brand-accent" },
    { key: "completed", label: "Concluídas", value: s.completed ?? 0, className: "bg-success" },
    { key: "noAnswer", label: "Não atendeu", value: s.noAnswer ?? 0, className: "bg-warning" },
    { key: "busy", label: "Ocupado", value: s.busy ?? 0, className: "bg-status-ringing" },
    { key: "machine", label: "Caixa postal", value: s.machine ?? 0, className: "bg-status-paused" },
    { key: "abandoned", label: "Abandonadas", value: s.abandoned ?? 0, className: "bg-danger-border" },
    { key: "failed", label: "Falhas", value: s.failed ?? 0, className: "bg-danger" },
    { key: "cancelled", label: "Cancelados", value: s.cancelled ?? 0, className: "bg-status-offline" },
    { key: "dnc", label: "DNC", value: s.dnc ?? 0, className: "bg-fg-muted" },
  ];
}

/** Barra empilhada de status (CSS puro). `compact` = fina e sem legenda. */
export function StatusStackedBar({ stats, compact }: { stats: CampaignStatsDto; compact?: boolean }) {
  const segs = statusSegments(stats);
  const total = segs.reduce((a, s) => a + s.value, 0);
  return (
    <div className="space-y-4">
      <div
        className={cn("flex w-full gap-px overflow-hidden rounded-full bg-surface-sunken", compact ? "h-1.5" : "h-3")}
        role="img"
        aria-label={total ? segs.map((s) => `${s.label}: ${s.value}`).join(", ") : "Sem contatos"}
      >
        {total > 0
          ? segs
              .filter((s) => s.value > 0)
              .map((s) => (
                <div
                  key={s.key}
                  className={cn("h-full min-w-1 transition-[width] duration-500", s.className)}
                  style={{ width: `${(s.value / total) * 100}%` }}
                  title={`${s.label}: ${s.value}`}
                />
              ))
          : null}
      </div>
      {!compact ? (
        <ul className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
          {segs.map((s) => (
            <li key={s.key} className={cn("flex min-w-0 items-center gap-2 text-body-sm", s.value === 0 && "opacity-60")}>
              <span className={cn("h-2.5 w-2.5 shrink-0 rounded-sm", s.className)} aria-hidden />
              <span className="min-w-0 flex-1 truncate text-fg-muted">{s.label}</span>
              <span className="tnum font-semibold text-fg">{s.value}</span>
              <span className="tnum hidden w-10 text-right text-caption text-fg-subtle sm:inline">
                {total > 0 ? `${Math.round((s.value / total) * 100)}%` : "—"}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Barra de progresso da base (resolvidos / total). */
export function CampaignProgressBar({ stats, className }: { stats?: CampaignStatsDto; className?: string }) {
  const p = campaignProgress(stats);
  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-baseline justify-between gap-2 text-caption">
        <span className="text-fg-subtle">Progresso da base</span>
        <span className="tnum font-medium text-fg">
          {p.done.toLocaleString("pt-BR")}/{p.total.toLocaleString("pt-BR")}
          <span className="ml-1 text-fg-subtle">· {Math.round(p.pct)}%</span>
        </span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken"
        role="progressbar"
        aria-label="Progresso da base de contatos"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(p.pct)}
      >
        <div className="h-full rounded-full bg-brand-accent transition-[width] duration-500" style={{ width: `${p.pct}%` }} />
      </div>
    </div>
  );
}

const FUNNEL_SHADE = ["opacity-100", "opacity-90", "opacity-80", "opacity-70", "opacity-60"];

/** Funil: contatos → discados → atendidos (humano) → conectados → concluídos, com conversão entre etapas. */
export function StatusFunnel({ stats }: { stats: CampaignStatsDto }) {
  const total = stats.total ?? 0;
  const notDialed = (stats.pending ?? 0) + (stats.cancelled ?? 0) + (stats.dnc ?? 0);
  const answered = (stats.answered ?? 0) + (stats.connected ?? 0) + (stats.completed ?? 0) + (stats.abandoned ?? 0);
  const steps = [
    { label: "Contatos", value: total },
    { label: "Discados", value: Math.max(0, total - notDialed) },
    { label: "Atendidos (humano)", value: answered },
    { label: "Conectados", value: (stats.connected ?? 0) + (stats.completed ?? 0) },
    { label: "Concluídos", value: stats.completed ?? 0 },
  ];
  return (
    <ol className="space-y-2">
      {steps.map((s, i) => {
        const pct = total > 0 ? (s.value / total) * 100 : 0;
        const prev = i > 0 ? steps[i - 1].value : 0;
        const conv = i > 0 && prev > 0 ? Math.round((s.value / prev) * 100) : null;
        return (
          <li key={s.label} className="space-y-1">
            <div className="flex items-baseline justify-between gap-2 text-body-sm">
              <span className="min-w-0 truncate text-fg-muted">{s.label}</span>
              <span className="tnum shrink-0 font-semibold text-fg">
                {s.value.toLocaleString("pt-BR")}
                <span className="ml-1.5 text-caption font-normal text-fg-subtle">
                  {conv != null ? `${conv}% da etapa anterior` : `${Math.round(pct)}%`}
                </span>
              </span>
            </div>
            <div className="flex h-6 justify-center overflow-hidden rounded-md bg-surface-sunken">
              <div
                className={cn("h-full rounded-md bg-brand-accent transition-[width] duration-500", FUNNEL_SHADE[i])}
                style={{ width: `${Math.max(pct, s.value > 0 ? 2 : 0)}%` }}
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* --------------------------------------------------------- payload ---- */

export function PayloadView({ payload, empty = "Sem dados adicionais." }: { payload: unknown; empty?: string }) {
  const entries = payloadEntries(payload);
  if (!entries.length) return <p className="text-body-sm text-fg-subtle">{empty}</p>;
  return (
    <dl className="grid gap-x-4 gap-y-1.5 text-body-sm sm:grid-cols-[minmax(6rem,auto)_1fr]">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="truncate font-mono text-caption leading-5 text-fg-subtle">{k}</dt>
          <dd className="break-words font-medium text-fg">{v || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Editor chave/valor do payload do contato. */
export function KeyValueEditor({ rows, onChange }: { rows: KeyValueRow[]; onChange: (rows: KeyValueRow[]) => void }) {
  const dupKeys = new Set(
    rows
      .map((r) => r.key.trim())
      .filter((k, i, arr) => k && arr.indexOf(k) !== i),
  );
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.id} className="flex items-start gap-2">
          <div className="w-2/5">
            <Input
              placeholder="chave (ex.: imovel)"
              aria-label="Chave"
              value={r.key}
              className="font-mono"
              invalid={dupKeys.has(r.key.trim())}
              onChange={(e) => onChange(rows.map((x) => (x.id === r.id ? { ...x, key: e.target.value } : x)))}
            />
          </div>
          <div className="flex-1">
            <Input
              placeholder="valor"
              aria-label="Valor"
              value={r.value}
              onChange={(e) => onChange(rows.map((x) => (x.id === r.id ? { ...x, value: e.target.value } : x)))}
            />
          </div>
          <IconButton icon={Trash2} label="Remover campo" variant="danger-ghost" onClick={() => onChange(rows.filter((x) => x.id !== r.id))} />
        </div>
      ))}
      {dupKeys.size ? <p className="text-caption text-danger-fg">Há chaves repetidas.</p> : null}
      <Button type="button" variant="secondary" size="sm" icon={Plus} onClick={() => onChange([...rows, newKeyValueRow()])}>
        Adicionar campo
      </Button>
    </div>
  );
}

export function FieldError({ msg }: { msg?: string }) {
  return msg ? <p className="text-caption text-danger-fg">{msg}</p> : null;
}

/** Linha "rótulo: valor" compacta usada em cards de campanha e no drawer. */
export function MetaItem({ icon: Icon, children, className }: { icon?: typeof Copy; children: ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5 text-body-sm text-fg-muted", className)}>
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-fg-subtle" aria-hidden /> : null}
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}
